// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Box,
  Flashbar,
  Link,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-react-v3/polaris';
import { ChangedModel } from '../Settings/ChangeSetImpact/changeSetImpact';
import {
  ApplyProgress,
  applyProgressOf,
  changeSetRoute,
  failedApprovalMessage,
  isAwaitingApproval,
  isProposedChangeSet,
  LatestChangeSet,
  modelLabel,
  modelName,
  ProposedChangeSet,
} from '../Settings/ChangeSets/changeSets';
import { LocalesState } from '../../utils/locales';
import { ApiError } from '../../utils/serverErrors';
import { RetryAlert } from './ErrorMessages';

/**
 * Loading, saving and the alerts around a page that shows and edits the live version of a record.
 * Used by the base rates and TER group pages.
 */

export interface VersionedRecordState<T> {
  // Null while loading, when the load failed, or when there is no record yet.
  record: T | null;
  loading: boolean;
  loadErrors: string[];
  reload: () => void;
  savedVersion: number | null;
  dismissSaved: () => void;
  // Set when the last save was proposed for review instead of written.
  proposed: ProposedChangeSet | null;
  dismissProposed: () => void;
  /**
   * Sends a save and shows the record it resolves with. When it resolves with a change set
   * proposed for review instead, the live record, which Pricing left unchanged, stays shown and
   * any editor of it starts over. Rejects with the API error.
   */
  save: (send: () => Promise<T | ProposedChangeSet>) => Promise<void>;
}

/**
 * A copy of a record loaded as JSON, with new lists, since an editor starts over from its record
 * only when the record's lists change identity.
 */
function restartedCopyOf<T>(record: T): T {
  return JSON.parse(JSON.stringify(record));
}

/**
 * Loads the record with `fetchRecord`, which must be stable between renders unless what it loads
 * changed. With `notFoundIsEmpty`, a 404 means there is no record yet rather than an error.
 */
export function useVersionedRecord<T extends { version: number } & LatestChangeSet>(
  fetchRecord: () => Promise<T>,
  describeError: (error: ApiError) => string[],
  notFoundIsEmpty = false
): VersionedRecordState<T> {
  const [record, setRecord] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  const [savedVersion, setSavedVersion] = useState<number | null>(null);
  const [proposed, setProposed] = useState<ProposedChangeSet | null>(null);

  // Only the latest load may update the page, so an earlier one that settles later is ignored.
  const latestLoad = useRef(0);

  const reload = useCallback((): void => {
    latestLoad.current += 1;
    const load = latestLoad.current;
    const isLatest = (): boolean => load === latestLoad.current;
    setLoading(true);
    setLoadErrors([]);
    fetchRecord()
      .then(fetched => {
        if (isLatest()) {
          setRecord(fetched);
        }
      })
      .catch((error: ApiError) => {
        if (!isLatest()) {
          return;
        }
        setRecord(null);
        if (!notFoundIsEmpty || error.status !== 404) {
          setLoadErrors(describeError(error));
        }
      })
      .finally(() => {
        if (isLatest()) {
          setLoading(false);
        }
      });
  }, [fetchRecord, describeError, notFoundIsEmpty]);

  useEffect(reload, [reload]);

  const save = (send: () => Promise<T | ProposedChangeSet>): Promise<void> => {
    setSavedVersion(null);
    setProposed(null);
    return send().then(result => {
      if (isProposedChangeSet(result)) {
        setProposed(result);
        // Pricing points the record at its newest change set, so a reload would show the same.
        setRecord(
          current =>
            current && {
              ...restartedCopyOf(current),
              latestChangeSetId: result.changeSetId,
              latestChangeSetStatus: result.status,
            }
        );
        return;
      }
      setRecord(result);
      setSavedVersion(result.version);
    });
  };

  return {
    record,
    loading,
    loadErrors,
    reload,
    savedVersion,
    dismissSaved: (): void => setSavedVersion(null),
    proposed,
    dismissProposed: (): void => setProposed(null),
    save,
  };
}

interface VersionedRecordPageProps<T> {
  // Element ids start with this.
  idPrefix: string;
  page: VersionedRecordState<T>;
  locales: LocalesState;
  loadingText: string;
  loadErrorHeader: string;
  retryId: string;
  // Shown once loading is done.
  children: React.ReactNode;
}

const modelText = (model: ChangedModel): string =>
  `${modelName(model)} (${modelLabel(model.entityType)}${
    model.organizationId === null ? '' : `, organization ${model.organizationId}`
  })`;

/**
 * An inverted AWS-UI link, since a plain link takes the page's link color, which is the info
 * flash's background. Followed through the router, so the portal does not reload.
 */
const ChangeSetLink = withRouter(
  ({
    id,
    changeSetId,
    history,
  }: RouteComponentProps & { id: string; changeSetId: string }): JSX.Element => {
    const route = changeSetRoute(changeSetId);
    return (
      <Link
        id={id}
        color="inverted"
        href={route}
        onFollow={(event): void => {
          event.preventDefault();
          history.push(route);
        }}
      >
        Review the change set
      </Link>
    );
  }
);

/** That the save awaits review, with a link to the change set and the models it cannot regenerate. */
const ProposedMessage = ({ changeSet }: { changeSet: ProposedChangeSet }): JSX.Element => {
  const skippedModels = changeSet.skippedModels || [];
  return (
    <>
      <div>
        Live models depend on this record, so the update is pending review as change set{' '}
        {changeSet.changeSetId}. The live version below stays in use until it is approved.{' '}
        <ChangeSetLink id="proposedChangeSetLink" changeSetId={changeSet.changeSetId} />
      </div>
      {skippedModels.length > 0 && (
        <div>
          {`Not regenerated, because their live version has no stored inputs: ${skippedModels
            .map(modelText)
            .join('; ')}`}
        </div>
      )}
    </>
  );
};

interface AwaitingApproval {
  changeSetId: string;
  failed: boolean;
  // Only for a failed change set; null when the record's GET left its counts out.
  progress: ApplyProgress | null;
}

/** That the record's newest change set awaits approval, with a link to it. */
const AwaitingApprovalMessage = ({
  awaiting: { changeSetId, failed, progress },
}: {
  awaiting: AwaitingApproval;
}): JSX.Element => (
  <div>
    {failed
      ? failedApprovalMessage(progress)
      : `An update to this record is pending review as change set ${changeSetId}. The live version below stays in use until it is approved.`}{' '}
    <ChangeSetLink id="latestChangeSetLink" changeSetId={changeSetId} />
  </div>
);

/**
 * The record's newest change set while it awaits approval, unless the save's own flash already
 * names it.
 */
const changeSetAwaitingApproval = <T extends LatestChangeSet>({
  record,
  proposed,
}: VersionedRecordState<T>): AwaitingApproval | null => {
  if (record === null || !isAwaitingApproval(record)) {
    return null;
  }
  const changeSetId = record.latestChangeSetId;
  if (proposed !== null && proposed.changeSetId === changeSetId) {
    return null;
  }
  const failed = record.latestChangeSetStatus === 'failed';
  return {
    changeSetId,
    failed,
    progress: failed
      ? applyProgressOf(record.latestChangeSetAppliedModels, record.latestChangeSetTotalModels)
      : null,
  };
};

/** The save outcome, load and locale errors with their retries, then the page itself. */
export function VersionedRecordPage<T extends LatestChangeSet>({
  idPrefix,
  page,
  locales,
  loadingText,
  loadErrorHeader,
  retryId,
  children,
}: VersionedRecordPageProps<T>): JSX.Element {
  const awaiting = changeSetAwaitingApproval(page);
  return (
    <SpaceBetween size="m">
      {awaiting !== null && (
        <Flashbar
          id={`${idPrefix}AwaitingApproval`}
          items={[
            {
              type: 'info',
              content: <AwaitingApprovalMessage awaiting={awaiting} />,
            },
          ]}
        />
      )}
      {page.savedVersion !== null && (
        <Flashbar
          id={`${idPrefix}Saved`}
          items={[
            {
              type: 'success',
              content: `Saved as version ${page.savedVersion}`,
              dismissible: true,
              onDismiss: page.dismissSaved,
            },
          ]}
        />
      )}
      {page.proposed !== null && (
        <Flashbar
          id={`${idPrefix}Proposed`}
          items={[
            {
              type: 'info',
              content: <ProposedMessage changeSet={page.proposed} />,
              dismissible: true,
              onDismiss: page.dismissProposed,
            },
          ]}
        />
      )}
      {page.loadErrors.length > 0 && (
        <RetryAlert
          id={`${idPrefix}LoadError`}
          header={loadErrorHeader}
          messages={page.loadErrors}
          retryId={retryId}
          onRetry={page.reload}
        />
      )}
      {locales.errors.length > 0 && (
        <RetryAlert
          id={`${idPrefix}LocalesError`}
          header="Could not load the locales; editing is unavailable"
          messages={locales.errors}
          retryId="retryLocales"
          onRetry={locales.reload}
        />
      )}
      {page.loading ? (
        <Box id={`${idPrefix}Loading`} textAlign="center">
          <Spinner /> {loadingText}
        </Box>
      ) : (
        children
      )}
    </SpaceBetween>
  );
}
